import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  Avatar,
  Switch,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import BadgeIcon from "@mui/icons-material/Badge";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import GavelIcon from "@mui/icons-material/Gavel";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HandshakeIcon from "@mui/icons-material/Handshake";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import axios from "axios";
import moment from "moment";
import { useDropzone } from "react-dropzone";
import { getThemeTextColor, getThemeBackgroundColor } from "../../utils/themeUtils";
import { useTheme } from "@mui/material/styles";
import Notification from "../../components/Notification";
import { GlobalContext } from "../../contexts/GlobalContext";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";
import UBODialog from "../../components/UBODialog";
import AuthorizedSignatoryDialog from "../../components/AuthorizedSignatoryDialog";

const BAD_PRESS_CHECK_ID = 55;
const UBO_MANAGEMENT_ID = 56;
const AUTHORIZED_SIGNATORY_ID = 51;
const SEPA_MANDATE_ID = 57;
const TAX_COMPLIANCE_CERTIFICATE_ID = 59;

// Same upload behaviour as Organizational Chart (requires AllowPreFinance role)
const ORG_CHART_LIKE_DOC_IDS = [52, 57, 58, 60];

// Required for pre-finance (SEPA / id 57 is intentionally excluded)
const PRE_FINANCE_REQUIRED_DOC_IDS = [50, 51, 52, 58, 59, 60];

const documentTypes = [
  { id: 50, name: "KvK/CoC", code: "kvk_coc", icon: <DescriptionIcon sx={{ fontSize: 40 }} />, multiDocument: true },
  { id: 51, name: "Authorized Signatory ID", code: "id_passport", icon: <BadgeIcon sx={{ fontSize: 40 }} />, multiDocument: true },
  { id: 52, name: "Organizational Chart", code: "org_chart", icon: <AccountTreeIcon sx={{ fontSize: 40 }} />, multiDocument: false },
  {
    id: SEPA_MANDATE_ID,
    name: "SEPA Mandate",
    code: "sepa_mandate",
    icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
    multiDocument: false,
    requiresStartDate: true,
  },
  { id: 58, name: "Delta Agreement", code: "delta_agreement", icon: <HandshakeIcon sx={{ fontSize: 40 }} />, multiDocument: false },
  {
    id: TAX_COMPLIANCE_CERTIFICATE_ID,
    name: "Tax Compliance Certificate",
    code: "tax_compliance_certificate",
    icon: <VerifiedUserIcon sx={{ fontSize: 40 }} />,
    multiDocument: false,
    hasTimeRestriction: true,
  },
  {
    id: 60,
    name: "Assignment of Receivables",
    code: "assignment_of_receivables",
    icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
    multiDocument: false,
  },
  { id: BAD_PRESS_CHECK_ID, name: "Bad Press Check", code: "bad_press_check", icon: <GavelIcon sx={{ fontSize: 40 }} />, multiDocument: false },
  { id: UBO_MANAGEMENT_ID, name: "UBO Management", code: "ubo_management", icon: <PersonAddIcon sx={{ fontSize: 40 }} />, multiDocument: false },
];

const isDocumentWithinValidityPeriod = (doc) => {
  if (!doc?.validFrom || !doc?.validTill) {
    return false;
  }
  const now = moment();
  return moment(doc.validFrom).isSameOrBefore(now, "day") && moment(doc.validTill).isSameOrAfter(now, "day");
};

const getTaxComplianceValidityStatus = (doc) => {
  if (!doc) {
    return null;
  }
  if (!doc.validFrom || !doc.validTill) {
    return "incomplete";
  }
  if (!isDocumentWithinValidityPeriod(doc)) {
    return "expired";
  }
  const daysUntilExpiry = moment(doc.validTill).diff(moment(), "days");
  if (daysUntilExpiry <= 30) {
    return "expiring";
  }
  return "valid";
};

const CompanyDocumentsTab = ({ customerId }) => {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewDialog, setViewDialog] = useState({ open: false, document: null });
  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "" });
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPreviewDoc, setSelectedPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [documentFilenames, setDocumentFilenames] = useState({});
  const [ubos, setUbos] = useState([]);
  const [uboDialog, setUboDialog] = useState({
    open: false,
    ubo: null,
    mode: "list",
    selectedUbo: null,
  });
  const fileInputRef = useRef(null);
  const { globalData, fetchUserData } = useContext(GlobalContext);
  const { hasPermission } = usePermissions();
  const canWriteDocuments = hasPermission(CAPABILITIES.customersDocuments, "W");
  const canWriteCustomerCard = hasPermission(CAPABILITIES.customersCardWrite, "W");
  const canWriteVfFlags = hasPermission(CAPABILITIES.customersVfFlags, "W");
  const canWriteCustomerFiles = canWriteDocuments || canWriteCustomerCard;
  const [isContextLoading, setIsContextLoading] = useState(true);
  const [badPressCheck, setBadPressCheck] = useState({
    isNotUHRCCountry: false,
    noFinancialCrimes: false,
    noFinancialMisconduct: false,
    noNegativePublications: false,
    notOnSanctionList: false,
    noEnvironmentalViolations: false,
    noReputationDamage: false,
  });
  const [tempBadPressCheck, setTempBadPressCheck] = useState(null);
  const [badPressCheckId, setBadPressCheckId] = useState(null);
  const [blockPreFinance, setBlockPreFinance] = useState({
    isBlocked: false,
    reason: "",
    allowPreFinance: false,
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, uboId: null });
  const [isLoading, setIsLoading] = useState(false);
  const [uboPreviewUrls, setUboPreviewUrls] = useState({
    idDocument: null,
    pepDeclaration: null,
  });
  const [signatories, setSignatories] = useState([]);
  const [signatoryDialog, setSignatoryDialog] = useState({
    open: false,
    signatory: null,
    mode: "list",
    selectedSignatory: null,
  });
  const [signatoryPreviewUrls, setSignatoryPreviewUrls] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [blockPreFinanceTemp, setBlockPreFinanceTemp] = useState({
    isBlocked: false,
    reason: "",
  });
  const [dateInputDialog, setDateInputDialog] = useState({
    open: false,
    file: null,
    validFrom: null,
    validTill: null,
  });

  const hasAllowPreFinanceRole = () => canWriteCustomerFiles;

  // Initialize context data
  useEffect(() => {
    const initializeContext = async () => {
      try {
        if (!globalData?.userId) {
          setIsContextLoading(true);
          await fetchUserData();
        }
      } catch (error) {
        console.error("Error initializing context:", error);
      } finally {
        setIsContextLoading(false);
      }
    };

    initializeContext();
  }, [fetchUserData, globalData?.userId]);

  // Single useEffect for logging global context data
  useEffect(() => {
    if (globalData?.userId) {
      console.log("Global Context Data:", {
        userId: globalData.userId,
        email: globalData.email,
        roles: globalData.roles,
        displayName: globalData.displayName,
      });
    }
  }, [globalData?.userId]);

  // Get the current user ID from the context
  const getCurrentUserId = () => {
    if (!globalData?.userId) {
      console.error("Global context data is not available");
      return null;
    }
    return globalData.userId;
  };

  // Fetch documents on component mount
  useEffect(() => {
    if (!customerId || !globalData?.userId) return;
    const loadAllData = async () => {
      setIsDataLoading(true);
      try {
        await Promise.all([fetchDocuments(), fetchBadPressCheck(), fetchBlockPreFinance(), fetchSignatories(), fetchUbos()]);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsDataLoading(false);
      }
    };
    loadAllData();
  }, [customerId, globalData?.userId]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
      setDocuments(response.data || []);

      // Fetch filenames for all documents
      const newFilenames = {};
      await Promise.all(
        response.data.map(async (doc) => {
          try {
            const res = await axios.get(`/api/v1/file/${doc.fileId}`);
            newFilenames[doc.fileId] = res.data?.fileName || "";
          } catch {
            newFilenames[doc.fileId] = "";
          }
        })
      );
      setDocumentFilenames(newFilenames);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    }
  };

  const fetchBadPressCheck = async () => {
    try {
      const response = await axios.get(`/api/v1/badpresschecks/entity/${customerId}`);
      if (response.data) {
        setBadPressCheck({
          isNotUHRCCountry: response.data.isNotUHRCCountry,
          noFinancialCrimes: response.data.noFinancialCrimes,
          noFinancialMisconduct: response.data.noFinancialMisconduct,
          noNegativePublications: response.data.noNegativePublications,
          notOnSanctionList: response.data.notOnSanctionList,
          noEnvironmentalViolations: response.data.noEnvironmentalViolations,
          noReputationDamage: response.data.noReputationDamage,
        });
        setBadPressCheckId(response.data.id);
      }
    } catch (error) {
      console.error("Error fetching bad press check:", error);
    }
  };

  const fetchBlockPreFinance = async () => {
    try {
      const response = await axios.get(`/api/v1/entitySettings/${customerId}`);
      if (response.data) {
        setBlockPreFinance({
          isBlocked: response.data.blockedPreFinance || false,
          reason: response.data.blockedPreFinanceReason || "",
          allowPreFinance: response.data.allowPreFinance || false,
        });
      }
    } catch (error) {
      console.error("Error fetching entity settings:", error);
      // If 404, create default settings
      if (error.response?.status === 404) {
        try {
          const defaultSettings = {
            entityId: customerId,
            allowPreFinance: false,
            blockedPreFinance: false,
            blockedPreFinanceReason: "",
            modificationUser: getCurrentUserId(),
          };
          await axios.post("/api/v1/entitySettings", defaultSettings);
          setBlockPreFinance({
            isBlocked: false,
            reason: "",
            allowPreFinance: false,
          });
        } catch (createError) {
          console.error("Error creating default entity settings:", createError);
        }
      }
    }
  };

  const fetchSignatories = async () => {
    try {
      const response = await axios.get(`/api/v1/customerauthorizedsignatories/entity/${customerId}`);
      const signatoryData = response.data;
      const signatories = Array.isArray(signatoryData) ? signatoryData : signatoryData ? [signatoryData] : [];

      // Fetch filenames for signatory documents
      const newFilenames = { ...documentFilenames };
      await Promise.all(
        signatories.map(async (signatory) => {
          try {
            if (signatory.idPassportFileId) {
              const res = await axios.get(`/api/v1/file/${signatory.idPassportFileId}`);
              newFilenames[signatory.idPassportFileId] = res.data?.fileName || "";
            }
          } catch (error) {
            console.error("Error fetching signatory document filename:", error);
          }
        })
      );
      setDocumentFilenames((prevFilenames) => ({ ...prevFilenames, ...newFilenames }));
      setSignatories(signatories);
    } catch (error) {
      console.error("Error fetching signatories:", error);
      setSignatories([]);
    }
  };

  const fetchUbos = async () => {
    try {
      const response = await axios.get(`/api/v1/customerubos/entity/${customerId}`);
      const uboData = response.data;
      const ubos = Array.isArray(uboData) ? uboData : uboData ? [uboData] : [];

      // Fetch filenames for UBO documents
      const newFilenames = { ...documentFilenames };
      await Promise.all(
        ubos.map(async (ubo) => {
          try {
            if (ubo.idPassportFileId) {
              const res = await axios.get(`/api/v1/file/${ubo.idPassportFileId}`);
              newFilenames[ubo.idPassportFileId] = res.data?.fileName || "";
            }
            if (ubo.pepFileId) {
              const res = await axios.get(`/api/v1/file/${ubo.pepFileId}`);
              newFilenames[ubo.pepFileId] = res.data?.fileName || "";
            }
          } catch (error) {
            console.error("Error fetching UBO document filename:", error);
          }
        })
      );
      setDocumentFilenames((prevFilenames) => ({ ...prevFilenames, ...newFilenames }));
      setUbos(ubos);
    } catch (error) {
      console.error("Error fetching UBOs:", error);
      setUbos([]);
    }
  };

  const handleDocumentClick = async (docType) => {
    if (docType.id === BAD_PRESS_CHECK_ID) {
      // Special handling for Bad Press Check
      setTempBadPressCheck(badPressCheck); // Initialize temp state with current values
      setViewDialog({ open: true, document: docType });
      return;
    }

    if (docType.id === AUTHORIZED_SIGNATORY_ID) {
      // Special handling for Authorized Signatory ID
      handleSignatoryClick();
      return;
    }

    const docs = documents.filter((doc) => doc.documentType === docType.id && doc.deleted === 0);
    setSelectedDocument({ type: docType, documents: docs });
    setViewDialog({ open: true, document: docType });

    // If there are documents, automatically select and preview the first one
    if (docs.length > 0) {
      const firstDoc = docs[0];
      setSelectedPreviewDoc(firstDoc);
      try {
        const response = await axios({
          url: `/api/v1/azureblobstorage/${firstDoc.fileId}`,
          method: "GET",
          responseType: "blob",
        });
        const href = URL.createObjectURL(response.data);
        setPreviewUrl(href);
      } catch (error) {
        console.error("Error loading preview:", error);
        setNotify({ isOpen: true, message: "Error loading document preview.", type: "error" });
      }
    }
  };

  const handlePreviewDocument = async (doc) => {
    setSelectedPreviewDoc(doc);
    try {
      const response = await axios({
        url: `/api/v1/azureblobstorage/${doc.fileId}`,
        method: "GET",
        responseType: "blob",
      });
      const href = URL.createObjectURL(response.data);
      setPreviewUrl(href);
    } catch (error) {
      console.error("Error loading preview:", error);
      setNotify({ isOpen: true, message: "Error loading document preview.", type: "error" });
    }
  };

  // Cleanup preview URL when component unmounts or dialog closes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleCloseDialog = () => {
    setViewDialog({ open: false, document: null });
    setSelectedDocument(null);
    setSelectedFile(null);
    setSelectedPreviewDoc(null);
    setTempBadPressCheck(null); // Reset temp state when dialog closes
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      handlePendingFileUpload(file);
    }
  };

  const handlePendingFileUpload = (file) => {
    if (selectedDocument?.type?.hasTimeRestriction || selectedDocument?.type?.requiresStartDate) {
      setDateInputDialog({ open: true, file, validFrom: null, validTill: null });
      return;
    }
    handleFileUpload(file);
  };

  const handleDateInputCancel = () => {
    setDateInputDialog({ open: false, file: null, validFrom: null, validTill: null });
    setSelectedFile(null);
  };

  const handleDateInputConfirm = async () => {
    const { file, validFrom, validTill } = dateInputDialog;
    const requiresStartDateOnly = selectedDocument?.type?.requiresStartDate;

    if (requiresStartDateOnly) {
      if (!validFrom || !moment(validFrom).isValid()) {
        setNotify({ isOpen: true, message: "Please fill in the start date.", type: "warning" });
        return;
      }
      setDateInputDialog({ open: false, file: null, validFrom: null, validTill: null });
      await handleFileUpload(file, validFrom, null);
      return;
    }

    if (!validFrom || !validTill || !moment(validFrom).isValid() || !moment(validTill).isValid()) {
      setNotify({ isOpen: true, message: "Please fill in both Valid From and Valid Till dates.", type: "warning" });
      return;
    }
    if (moment(validTill).isBefore(moment(validFrom), "day")) {
      setNotify({ isOpen: true, message: "Valid Till must be on or after Valid From.", type: "warning" });
      return;
    }
    setDateInputDialog({ open: false, file: null, validFrom: null, validTill: null });
    await handleFileUpload(file, validFrom, validTill);
  };

  const handleFileUpload = async (file, validFrom = null, validTill = null) => {
    if (!selectedDocument || !customerId) {
      setNotify({ isOpen: true, message: "Please select a document type first.", type: "error" });
      return;
    }

    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      setNotify({
        isOpen: true,
        message: "Error: User ID not available. Please try again.",
        type: "error",
      });
      return;
    }

    // Check for existing document if it's not a multi-document type
    if (!selectedDocument.type.multiDocument) {
      const existingDoc = documents.find((doc) => doc.documentType === selectedDocument.type.id && doc.deleted === 0);

      if (existingDoc) {
        setNotify({
          isOpen: true,
          message: `A ${selectedDocument.type.name} already exists. Please delete the existing document first.`,
          type: "warning",
        });
        return;
      }
    }

    try {
      setNotify({ isOpen: true, message: "Uploading document...", type: "info" });

      // 1. POST file info
      const fileInfo = {
        documentType: selectedDocument.type.id,
        creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        createdBy: currentUserId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      };
      const fileInfoRes = await axios.post("/api/v1/file", fileInfo);
      const fileId = fileInfoRes.data.fileId;

      // 2. POST file to Azure
      const formData = new FormData();
      formData.append("file", file, fileId);
      await axios.post("/api/v1/azureblobstorage", formData);

      // 3. POST document info
      const formattedValidFrom = validFrom ? moment(validFrom).format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z" : null;
      const formattedValidTill = validTill ? moment(validTill).format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z" : null;

      const documentInfo = {
        entityId: customerId,
        documentType: selectedDocument.type.id,
        documentVersion: 1,
        uploadedBy: currentUserId,
        uploadDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        fileId: fileId,
        deleted: 0,
        countryId: 0,
        validFrom: formattedValidFrom,
        validTill: formattedValidTill,
        hasTimeRestriction: Boolean(
          (formattedValidFrom && formattedValidTill) ||
            (formattedValidFrom && selectedDocument.type.requiresStartDate)
        ),
        vatRequired: false,
        edRequired: false,
      };
      const documentResponse = await axios.post("/api/v1/customerdocuments", documentInfo);
      const newDocument = documentResponse.data;

      setNotify({ isOpen: true, message: "Document uploaded successfully!", type: "success" });
      setSelectedFile(null);

      // Fetch updated documents
      const response = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
      const updatedDocuments = response.data || [];
      setDocuments(updatedDocuments);

      // Update the selected document's documents list
      const updatedDocs = updatedDocuments.filter((doc) => doc.documentType === selectedDocument.type.id && doc.deleted === 0);
      setSelectedDocument((prev) => ({
        ...prev,
        documents: updatedDocs,
      }));

      // Update filenames
      const newFilenames = { ...documentFilenames };
      newFilenames[fileId] = file.name;
      setDocumentFilenames(newFilenames);

      // Automatically select and preview the newly uploaded document
      setSelectedPreviewDoc(newDocument);
      try {
        const previewResponse = await axios({
          url: `/api/v1/azureblobstorage/${fileId}`,
          method: "GET",
          responseType: "blob",
        });
        const href = URL.createObjectURL(previewResponse.data);
        setPreviewUrl(href);
      } catch (error) {
        console.error("Error loading preview:", error);
        setNotify({ isOpen: true, message: "Error loading document preview.", type: "error" });
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      setNotify({
        isOpen: true,
        message: "Error uploading document: " + (error.response?.data?.message || error.message),
        type: "error",
      });
    }
  };

  const handleDeleteDocument = async (doc) => {
    try {
      await axios.put(`/api/v1/customerdocuments/${doc.customerDocumentId}`, {
        ...doc,
        deleted: 1,
      });
      setNotify({ isOpen: true, message: "Document deleted successfully!", type: "success" });

      // Fetch updated documents
      const response = await axios.get(`/api/v1/customerdocuments/entity/${customerId}`);
      const updatedDocuments = response.data || [];
      setDocuments(updatedDocuments);

      // Update the selected document's documents list
      const updatedDocs = updatedDocuments.filter((doc) => doc.documentType === selectedDocument.type.id && doc.deleted === 0);
      setSelectedDocument((prev) => ({
        ...prev,
        documents: updatedDocs,
      }));

      // Clear preview if the deleted document was being previewed
      if (selectedPreviewDoc?.customerDocumentId === doc.customerDocumentId) {
        setSelectedPreviewDoc(null);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
      }

      // Check if requirements are still met after deletion
      const newStatus = getPreFinanceStatus();
      if (newStatus.status === "incomplete") {
        try {
          // Save the changes to the database first
          await axios.put(`/api/v1/entitySettings/${customerId}`, {
            entityId: customerId,
            allowPreFinance: blockPreFinance.allowPreFinance,
            blockedPreFinance: true,
            blockedPreFinanceReason: "Please complete all required documents",
            modificationUser: getCurrentUserId(),
          });

          // Update both temporary and main state
          setBlockPreFinanceTemp({
            isBlocked: true,
            reason: "Please complete all required documents",
          });
          setBlockPreFinance((prev) => ({
            ...prev,
            isBlocked: true,
            reason: "Please complete all required documents",
          }));
          setNotify({
            isOpen: true,
            message: "Pre-Finance has been automatically blocked due to missing requirements.",
            type: "warning",
          });
        } catch (error) {
          console.error("Error updating entity settings:", error);
          setNotify({ isOpen: true, message: "Error updating Pre-Finance settings.", type: "error" });
        }
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      setNotify({ isOpen: true, message: "Error deleting document.", type: "error" });
    }
  };

  const downloadFile = async (fileId) => {
    try {
      const response = await axios({
        url: `/api/v1/azureblobstorage/${fileId}`,
        method: "GET",
        responseType: "blob",
      });
      const href = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = documentFilenames[fileId] || "document.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (error) {
      console.error("Error downloading file:", error);
      setNotify({ isOpen: true, message: "Error downloading file.", type: "error" });
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled:
      !hasAllowPreFinanceRole() &&
      (ORG_CHART_LIKE_DOC_IDS.includes(selectedDocument?.type.id) ||
        selectedDocument?.type.id === TAX_COMPLIANCE_CERTIFICATE_ID),
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setSelectedFile(acceptedFiles[0]);
        handlePendingFileUpload(acceptedFiles[0]);
      }
    },
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
  });

  const handleBadPressCheckChange = (field) => (event) => {
    setTempBadPressCheck((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleBadPressCheckSubmit = async () => {
    try {
      const data = {
        entityId: customerId,
        ...tempBadPressCheck,
        createdBy: getCurrentUserId(),
        modifiedBy: getCurrentUserId(),
      };

      if (badPressCheckId) {
        await axios.put(`/api/v1/badpresschecks/${badPressCheckId}`, {
          ...data,
          id: badPressCheckId,
        });
      } else {
        await axios.post("/api/v1/badpresschecks", data);
      }

      // Update the main state with the saved changes
      setBadPressCheck(tempBadPressCheck);

      // Check if requirements are still met after saving
      if (!isBadPressCheckComplete(tempBadPressCheck)) {
        try {
          // Save the changes to the database first
          await axios.put(`/api/v1/entitySettings/${customerId}`, {
            entityId: customerId,
            allowPreFinance: blockPreFinance.allowPreFinance,
            blockedPreFinance: true,
            blockedPreFinanceReason: "Please complete all required documents",
            modificationUser: getCurrentUserId(),
          });

          // Update both temporary and main state
          setBlockPreFinanceTemp({
            isBlocked: true,
            reason: "Please complete all required documents",
          });
          setBlockPreFinance((prev) => ({
            ...prev,
            isBlocked: true,
            reason: "Please complete all required documents",
          }));
          setNotify({
            isOpen: true,
            message: "Pre-Finance has been automatically blocked due to missing requirements.",
            type: "warning",
          });
        } catch (error) {
          console.error("Error updating entity settings:", error);
          setNotify({ isOpen: true, message: "Error updating Pre-Finance settings.", type: "error" });
        }
      }

      setNotify({ isOpen: true, message: "Bad Press Check updated successfully!", type: "success" });
      handleCloseDialog();
      fetchBadPressCheck();
    } catch (error) {
      console.error("Error updating bad press check:", error);
      setNotify({
        isOpen: true,
        message: "Error updating Bad Press Check: " + (error.response?.data?.message || error.message),
        type: "error",
      });
    }
  };

  const isBadPressCheckComplete = (checks) => {
    return (
      checks.isNotUHRCCountry &&
      checks.noFinancialCrimes &&
      checks.noFinancialMisconduct &&
      checks.noNegativePublications &&
      checks.notOnSanctionList &&
      checks.noEnvironmentalViolations &&
      checks.noReputationDamage
    );
  };

  const handleUboClick = async () => {
    // First fetch the UBOs
    await fetchUbos();

    // Then open the dialog and select the first UBO if available
    setUboDialog({
      open: true,
      ubo: null,
      mode: "list",
      selectedUbo: ubos.length > 0 ? ubos[0] : null,
    });

    // If there's a UBO to select, trigger the preview loading
    if (ubos.length > 0) {
      handleUboSelect(ubos[0]);
    }
  };

  const handleUboClose = () => {
    // Cleanup preview URLs
    if (uboPreviewUrls.idDocument) {
      URL.revokeObjectURL(uboPreviewUrls.idDocument);
    }
    if (uboPreviewUrls.pepDeclaration) {
      URL.revokeObjectURL(uboPreviewUrls.pepDeclaration);
    }
    setUboPreviewUrls({ idDocument: null, pepDeclaration: null });
    setUboDialog({ open: false, ubo: null, mode: "list", selectedUbo: null });
  };

  const handleUboSelect = async (ubo) => {
    setUboDialog((prev) => ({ ...prev, selectedUbo: ubo }));

    // Clear previous previews
    setUboPreviewUrls({ idDocument: null, pepDeclaration: null });

    // Load both previews
    const loadPreview = async (fileId, type) => {
      if (!fileId) return;
      try {
        const response = await axios({
          url: `/api/v1/azureblobstorage/${fileId}`,
          method: "GET",
          responseType: "blob",
        });
        const href = URL.createObjectURL(response.data);
        setUboPreviewUrls((prev) => ({ ...prev, [type]: href }));
      } catch (error) {
        console.error(`Error loading ${type} preview:`, error);
        setNotify({ isOpen: true, message: `Error loading ${type} preview.`, type: "error" });
      }
    };

    // Load both documents in parallel
    await Promise.all([loadPreview(ubo.idPassportFileId, "idDocument"), loadPreview(ubo.pepFileId, "pepDeclaration")]);
  };

  const handleUboEdit = (ubo) => {
    setUboDialog({
      open: true,
      ubo: {
        id: ubo.id,
        name: ubo.fullName,
        idDocument: ubo.idPassportFileId,
        pepDeclaration: ubo.pepFileId,
        badPressCheck: {
          noFinancialCrimes: ubo.noFinancialCrimes,
          noFinancialMisconduct: ubo.noFinancialMisconduct,
          noNegativePublications: ubo.noNegativePublications,
          notOnSanctionList: ubo.notOnSanctionList,
          noEnvironmentalViolations: ubo.noEnvironmentalViolations,
          noReputationDamage: ubo.noReputationDamage,
        },
      },
      mode: "edit",
      selectedUbo: ubo,
    });
  };

  const handleUboAdd = () => {
    setUboDialog({ open: true, ubo: { name: "", idDocument: null, pepDeclaration: null }, mode: "edit" });
  };

  const handleUboSubmit = async (uboData) => {
    try {
      setIsLoading(true);
      const currentUserId = getCurrentUserId();

      if (!currentUserId) {
        setNotify({
          isOpen: true,
          message: "Error: User ID not available. Please try again.",
          type: "error",
        });
        return;
      }

      const uboPayload = {
        entityId: customerId,
        fullName: uboData.name,
        idPassportFileId: uboData.idDocument,
        pepFileId: uboData.pepDeclaration,
        noFinancialCrimes: uboData.badPressCheck.noFinancialCrimes,
        noFinancialMisconduct: uboData.badPressCheck.noFinancialMisconduct,
        noNegativePublications: uboData.badPressCheck.noNegativePublications,
        notOnSanctionList: uboData.badPressCheck.notOnSanctionList,
        noEnvironmentalViolations: uboData.badPressCheck.noEnvironmentalViolations,
        noReputationDamage: uboData.badPressCheck.noReputationDamage,
        createdBy: currentUserId,
        modifiedBy: currentUserId,
      };

      let response;
      if (uboData.id) {
        // Update existing UBO
        response = await axios.put(`/api/v1/customerubos/${uboData.id}`, {
          ...uboPayload,
          id: uboData.id,
        });
      } else {
        // Create new UBO
        response = await axios.post("/api/v1/customerubos", uboPayload);
      }

      setNotify({ isOpen: true, message: "UBO saved successfully!", type: "success" });

      // Refresh the UBOs list
      await fetchUbos();

      // If we're editing, update the selected UBO with the new data
      if (uboData.id && uboDialog.selectedUbo?.id === uboData.id) {
        setUboDialog((prev) => ({
          ...prev,
          selectedUbo: response.data,
        }));
      }

      handleUboClose();
    } catch (error) {
      console.error("Error saving UBO:", error);
      setNotify({
        isOpen: true,
        message: "Error saving UBO: " + (error.response?.data?.message || error.message),
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUboDelete = async (uboId) => {
    setDeleteConfirmDialog({ open: true, uboId });
  };

  const handleBlockPreFinanceChange = (e) => {
    const { checked } = e.target;
    // Only allow unchecking if requirements are met
    if (!checked && getPreFinanceStatus().status === "incomplete") {
      setNotify({
        isOpen: true,
        message: "Cannot disable Block Pre-Finance while requirements are not met.",
        type: "warning",
      });
      return;
    }
    setBlockPreFinanceTemp((prev) => ({
      ...prev,
      isBlocked: checked,
    }));
  };

  const handleBlockReasonChange = (e) => {
    const { value } = e.target;
    setBlockPreFinanceTemp((prev) => ({
      ...prev,
      reason: value,
    }));
  };

  const handleSaveBlockPreFinance = () => {
    // Check if trying to disable block when requirements are not met
    if (!blockPreFinanceTemp.isBlocked && getPreFinanceStatus().status === "incomplete") {
      setNotify({
        isOpen: true,
        message: "Cannot disable Block Pre-Finance while requirements are not met.",
        type: "warning",
      });
      return;
    }

    setConfirmDialog({
      open: true,
      title: "Save Block Pre-Finance Settings",
      message: blockPreFinanceTemp.isBlocked
        ? "Are you sure you want to block pre-finance for this customer?"
        : "Are you sure you want to unblock pre-finance for this customer?",
      onConfirm: async () => {
        try {
          await axios.put(`/api/v1/entitySettings/${customerId}`, {
            entityId: customerId,
            allowPreFinance: blockPreFinance.allowPreFinance,
            blockedPreFinance: blockPreFinanceTemp.isBlocked,
            blockedPreFinanceReason: blockPreFinanceTemp.reason,
            modificationUser: getCurrentUserId(),
          });

          // Update the main state after successful save
          setBlockPreFinance((prev) => ({
            ...prev,
            isBlocked: blockPreFinanceTemp.isBlocked,
            reason: blockPreFinanceTemp.reason,
          }));

          setNotify({ isOpen: true, message: "Block pre-finance settings updated successfully!", type: "success" });
        } catch (error) {
          console.error("Error updating entity settings:", error);
          setNotify({ isOpen: true, message: "Error updating block pre-finance settings.", type: "error" });
        }
      },
    });
  };

  // Update temp state when main state changes (e.g., after initial load)
  useEffect(() => {
    setBlockPreFinanceTemp({
      isBlocked: blockPreFinance.isBlocked,
      reason: blockPreFinance.reason,
    });
  }, [blockPreFinance.isBlocked, blockPreFinance.reason]);

  const handleAllowPreFinanceChange = (e) => {
    const { checked } = e.target;
    setConfirmDialog({
      open: true,
      title: checked ? "Enable Pre-Finance" : "Disable Pre-Finance",
      message: checked ? "Are you sure you want to enable Pre-Finance?" : "Are you sure you want to disable Pre-Finance?",
      onConfirm: async () => {
        const newState = {
          ...blockPreFinance,
          allowPreFinance: checked,
        };
        setBlockPreFinance(newState);

        try {
          await axios.put(`/api/v1/entitySettings/${customerId}`, {
            entityId: customerId,
            allowPreFinance: checked,
            blockedPreFinance: blockPreFinance.isBlocked,
            blockedPreFinanceReason: blockPreFinance.reason,
            modificationUser: getCurrentUserId(),
          });
          setNotify({ isOpen: true, message: "Pre-finance settings updated successfully!", type: "success" });
        } catch (error) {
          console.error("Error updating entity settings:", error);
          setNotify({ isOpen: true, message: "Error updating pre-finance settings.", type: "error" });
          // Revert the state if the API call fails
          setBlockPreFinance((prev) => ({ ...prev, allowPreFinance: !checked }));
        }
      },
    });
  };

  const getDocumentStatus = (docType) => {
    if (docType.id === AUTHORIZED_SIGNATORY_ID) {
      return getSignatoryStatus();
    }
    const docs = documents.filter((doc) => doc.documentType === docType.id && doc.deleted === 0);
    if (docType.multiDocument) {
      return docs.length > 0 ? "complete" : "incomplete";
    }
    if (docs.length !== 1) {
      return "incomplete";
    }
    if (docType.hasTimeRestriction) {
      const validityStatus = getTaxComplianceValidityStatus(docs[0]);
      return validityStatus === "valid" || validityStatus === "expiring" ? "complete" : "incomplete";
    }
    return "complete";
  };

  const getUboStatus = () => {
    if (!Array.isArray(ubos) || ubos.length === 0) {
      return "incomplete";
    }
    const status = ubos.every(
      (ubo) =>
        ubo &&
        ubo.idPassportFileId &&
        ubo.pepFileId &&
        ubo.noFinancialCrimes &&
        ubo.noFinancialMisconduct &&
        ubo.noNegativePublications &&
        ubo.notOnSanctionList &&
        ubo.noEnvironmentalViolations &&
        ubo.noReputationDamage
    );
    return status ? "complete" : "incomplete";
  };

  const getBadPressStatus = () => {
    return isBadPressCheckComplete(badPressCheck) ? "complete" : "incomplete";
  };

  const getSignatoryStatus = () => {
    if (!Array.isArray(signatories) || signatories.length === 0) {
      return "incomplete";
    }
    const status = signatories.every((signatory) => signatory && signatory.idPassportFileId);
    return status ? "complete" : "incomplete";
  };

  const getPreFinanceStatus = () => {
    // First check if data is still loading
    if (isDataLoading) {
      return {
        status: "incomplete",
        reason: "Loading required data...",
      };
    }

    // Check if we have the required data
    if (!documents || !ubos || !signatories) {
      return {
        status: "incomplete",
        reason: "Required data is missing",
      };
    }

    const documentStatus = PRE_FINANCE_REQUIRED_DOC_IDS.every((docTypeId) => {
      if (docTypeId === AUTHORIZED_SIGNATORY_ID) {
        return getSignatoryStatus() === "complete";
      }
      const docs = documents.filter((doc) => doc.documentType === docTypeId && doc.deleted === 0);
      if (docTypeId === TAX_COMPLIANCE_CERTIFICATE_ID) {
        return docs.length > 0 && isDocumentWithinValidityPeriod(docs[0]);
      }
      return docs.length > 0;
    });

    const taxComplianceDoc = documents.find(
      (doc) => doc.documentType === TAX_COMPLIANCE_CERTIFICATE_ID && doc.deleted === 0
    );
    const taxComplianceExpired =
      taxComplianceDoc && taxComplianceDoc.validFrom && taxComplianceDoc.validTill && !isDocumentWithinValidityPeriod(taxComplianceDoc);
    const taxComplianceMissingDates = taxComplianceDoc && (!taxComplianceDoc.validFrom || !taxComplianceDoc.validTill);

    // Check if all UBOs are correct
    const uboStatus = getUboStatus() === "complete";

    // Check Bad Press Check status
    const badPressStatus = isBadPressCheckComplete(badPressCheck);

    // Check if all requirements are met
    const allRequirementsMet = documentStatus && uboStatus && badPressStatus;

    if (!documentStatus) {
      if (taxComplianceExpired) {
        return {
          status: "incomplete",
          reason: "Tax Compliance Certificate is outside its validity period",
        };
      }
      if (taxComplianceMissingDates) {
        return {
          status: "incomplete",
          reason: "Tax Compliance Certificate requires a Valid From and Valid Till date",
        };
      }
      return {
        status: "incomplete",
        reason: "Please complete all required documents",
      };
    }

    if (!uboStatus) {
      return {
        status: "incomplete",
        reason: "Please ensure all UBOs are complete and verified",
      };
    }

    if (!badPressStatus) {
      return {
        status: "incomplete",
        reason: "Please complete the Bad Press Check",
      };
    }

    return {
      status: "complete",
      reason: "All requirements met",
    };
  };

  useEffect(() => {
    return () => {
      if (uboPreviewUrls.idDocument) {
        URL.revokeObjectURL(uboPreviewUrls.idDocument);
      }
      if (uboPreviewUrls.pepDeclaration) {
        URL.revokeObjectURL(uboPreviewUrls.pepDeclaration);
      }
    };
  }, [uboPreviewUrls]);

  const handleSignatoryClick = async () => {
    await fetchSignatories();
    setSignatoryDialog({
      open: true,
      signatory: null,
      mode: "list",
      selectedSignatory: signatories.length > 0 ? signatories[0] : null,
    });
    if (signatories.length > 0) {
      handleSignatorySelect(signatories[0]);
    }
  };

  const handleSignatoryClose = () => {
    if (signatoryPreviewUrls.idDocument) {
      URL.revokeObjectURL(signatoryPreviewUrls.idDocument);
    }
    setSignatoryPreviewUrls({ idDocument: null });
    setSignatoryDialog({ open: false, signatory: null, mode: "list", selectedSignatory: null });
  };

  const handleSignatorySelect = async (signatory) => {
    setSignatoryDialog((prev) => ({ ...prev, selectedSignatory: signatory }));
    setSignatoryPreviewUrls({ idDocument: null });

    if (signatory.idPassportFileId) {
      try {
        const response = await axios({
          url: `/api/v1/azureblobstorage/${signatory.idPassportFileId}`,
          method: "GET",
          responseType: "blob",
        });
        const href = URL.createObjectURL(response.data);
        setSignatoryPreviewUrls({ idDocument: href });
      } catch (error) {
        console.error("Error loading signatory document preview:", error);
        setNotify({ isOpen: true, message: "Error loading document preview.", type: "error" });
      }
    }
  };

  const handleSignatoryEdit = (signatory) => {
    setSignatoryDialog({
      open: true,
      signatory: {
        id: signatory.id,
        name: signatory.fullName,
        comment: signatory.comment,
        idDocument: signatory.idPassportFileId,
      },
      mode: "edit",
      selectedSignatory: signatory,
    });
  };

  const handleSignatoryAdd = () => {
    setSignatoryDialog({
      open: true,
      signatory: { name: "", comment: "", idDocument: null },
      mode: "edit",
    });
  };

  const handleSignatorySubmit = async (signatoryData) => {
    try {
      setIsLoading(true);
      const currentUserId = getCurrentUserId();

      if (!currentUserId) {
        setNotify({
          isOpen: true,
          message: "Error: User ID not available. Please try again.",
          type: "error",
        });
        return;
      }

      // Validate required fields
      if (!signatoryData.name || !signatoryData.comment || !signatoryData.idDocument) {
        setNotify({
          isOpen: true,
          message: "Error: All fields are required.",
          type: "error",
        });
        return;
      }

      const payload = {
        entityId: customerId,
        fullName: signatoryData.name,
        comment: signatoryData.comment,
        idPassportFileId: signatoryData.idDocument,
      };

      let response;
      if (signatoryData.id) {
        // Update existing signatory
        response = await axios.put(`/api/v1/customerauthorizedsignatories/${signatoryData.id}`, {
          ...payload,
          modifiedBy: currentUserId,
        });
      } else {
        // Create new signatory
        response = await axios.post("/api/v1/customerauthorizedsignatories", {
          ...payload,
          createdBy: currentUserId,
        });
      }

      setNotify({ isOpen: true, message: "Authorized Signatory saved successfully!", type: "success" });
      await fetchSignatories();

      if (signatoryData.id && signatoryDialog.selectedSignatory?.id === signatoryData.id) {
        setSignatoryDialog((prev) => ({
          ...prev,
          selectedSignatory: response.data,
        }));
      }

      handleSignatoryClose();
    } catch (error) {
      console.error("Error saving Authorized Signatory:", error);
      setNotify({
        isOpen: true,
        message: "Error saving Authorized Signatory: " + (error.response?.data?.message || error.message),
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignatoryDelete = async (signatoryId) => {
    setDeleteConfirmDialog({ open: true, signatoryId });
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);
      if (deleteConfirmDialog.uboId) {
        await axios.delete(`/api/v1/customerubos/${deleteConfirmDialog.uboId}`);
        setNotify({ isOpen: true, message: "UBO deleted successfully!", type: "success" });
        await fetchUbos();

        // Check if requirements are still met after UBO deletion
        const newStatus = getPreFinanceStatus();
        if (newStatus.status === "incomplete") {
          try {
            // Save the changes to the database first
            await axios.put(`/api/v1/entitySettings/${customerId}`, {
              entityId: customerId,
              allowPreFinance: blockPreFinance.allowPreFinance,
              blockedPreFinance: true,
              blockedPreFinanceReason: "Please complete all required documents",
              modificationUser: getCurrentUserId(),
            });

            // Update both temporary and main state
            setBlockPreFinanceTemp({
              isBlocked: true,
              reason: "Please complete all required documents",
            });
            setBlockPreFinance((prev) => ({
              ...prev,
              isBlocked: true,
              reason: "Please complete all required documents",
            }));
            setNotify({
              isOpen: true,
              message: "Pre-Finance has been automatically blocked due to missing requirements.",
              type: "warning",
            });
          } catch (error) {
            console.error("Error updating entity settings:", error);
            setNotify({ isOpen: true, message: "Error updating Pre-Finance settings.", type: "error" });
          }
        }
      } else if (deleteConfirmDialog.signatoryId) {
        await axios.delete(`/api/v1/customerauthorizedsignatories/${deleteConfirmDialog.signatoryId}`);
        setNotify({ isOpen: true, message: "Authorized Signatory deleted successfully!", type: "success" });
        await fetchSignatories();

        // Check if requirements are still met after signatory deletion
        const newStatus = getPreFinanceStatus();
        if (newStatus.status === "incomplete") {
          try {
            // Save the changes to the database first
            await axios.put(`/api/v1/entitySettings/${customerId}`, {
              entityId: customerId,
              allowPreFinance: blockPreFinance.allowPreFinance,
              blockedPreFinance: true,
              blockedPreFinanceReason: "Please complete all required documents",
              modificationUser: getCurrentUserId(),
            });

            // Update both temporary and main state
            setBlockPreFinanceTemp({
              isBlocked: true,
              reason: "Please complete all required documents",
            });
            setBlockPreFinance((prev) => ({
              ...prev,
              isBlocked: true,
              reason: "Please complete all required documents",
            }));
            setNotify({
              isOpen: true,
              message: "Pre-Finance has been automatically blocked due to missing requirements.",
              type: "warning",
            });
          } catch (error) {
            console.error("Error updating entity settings:", error);
            setNotify({ isOpen: true, message: "Error updating Pre-Finance settings.", type: "error" });
          }
        }
      }
    } catch (error) {
      console.error("Error deleting:", error);
      setNotify({ isOpen: true, message: "Error deleting.", type: "error" });
    } finally {
      setIsLoading(false);
      setDeleteConfirmDialog({ open: false, uboId: null, signatoryId: null });
    }
  };

  // Add a new function to check and update pre-finance status
  const checkAndUpdatePreFinanceStatus = async () => {
    const newStatus = getPreFinanceStatus();
    if (newStatus.status === "incomplete") {
      try {
        // Save the changes to the database first
        await axios.put(`/api/v1/entitySettings/${customerId}`, {
          entityId: customerId,
          allowPreFinance: blockPreFinance.allowPreFinance,
          blockedPreFinance: true,
          blockedPreFinanceReason: "Please complete all required documents",
          modificationUser: getCurrentUserId(),
        });

        // Update both temporary and main state
        setBlockPreFinanceTemp({
          isBlocked: true,
          reason: "Please complete all required documents",
        });
        setBlockPreFinance((prev) => ({
          ...prev,
          isBlocked: true,
          reason: "Please complete all required documents",
        }));
      } catch (error) {
        console.error("Error updating entity settings:", error);
        setNotify({ isOpen: true, message: "Error updating Pre-Finance settings.", type: "error" });
      }
    }
  };

  // Add useEffect to check pre-finance status when documents, UBOs, or signatories change
  useEffect(() => {
    if (!isDataLoading) {
      checkAndUpdatePreFinanceStatus();
    }
  }, [documents, ubos, signatories, isDataLoading]);

  if (!globalData?.userId) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        {documentTypes.map((docType) => {
          const status = docType.id === UBO_MANAGEMENT_ID ? getUboStatus() : getDocumentStatus(docType);
          const isBadPress = docType.id === BAD_PRESS_CHECK_ID;
          const showGreenIcon = isBadPress ? getBadPressStatus() === "complete" : status === "complete";

          return (
            <Grid item xs={12} sm={6} md={4} key={docType.id}>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                  //opacity: isAllowed ? 1 : 0.5,
                }}
                onClick={() => (docType.id === UBO_MANAGEMENT_ID ? handleUboClick() : handleDocumentClick(docType))}
              >
                <Box sx={{ color: showGreenIcon ? "success.main" : "inherit" }}>{docType.icon}</Box>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  {docType.name}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  {showGreenIcon ? (
                    <CheckCircleIcon sx={{ color: "success.main", mr: 1 }} />
                  ) : (
                    <WarningIcon sx={{ color: "warning.main", mr: 1 }} />
                  )}
                  <Typography variant="body2" color="textSecondary">
                    {(() => {
                      //if (!isAllowed) return "Access restricted";
                      if (isBadPress) {
                        return getBadPressStatus() === "complete" ? "Passed Check" : "Not filled";
                      }
                      if (docType.id === UBO_MANAGEMENT_ID) {
                        if (ubos.length === 0) return "No UBOs added";
                        return `${ubos.length} UBO${ubos.length !== 1 ? "s" : ""} added`;
                      }
                      if (docType.id === AUTHORIZED_SIGNATORY_ID) {
                        if (signatories.length === 0) return "No signatories added";
                        return signatories.length === 1 ? "1 signatory added" : `${signatories.length} signatories added`;
                      }
                      const docs = documents.filter((doc) => doc.documentType === docType.id && doc.deleted === 0);
                      if (docs.length === 0) return "No document";
                      if (docType.hasTimeRestriction) {
                        const validityStatus = getTaxComplianceValidityStatus(docs[0]);
                        if (validityStatus === "expired") return "Document expired";
                        if (validityStatus === "incomplete") return "Validity dates missing";
                        if (validityStatus === "expiring") return "Document expiring soon";
                        return `Valid until ${moment(docs[0].validTill).format("DD-MM-YYYY")}`;
                      }
                      if (docType.requiresStartDate) {
                        if (!docs[0].validFrom) return "Start date missing";
                        return `Start date: ${moment(docs[0].validFrom).format("DD-MM-YYYY")}`;
                      }
                      return docType.multiDocument ? `${docs.length} document${docs.length !== 1 ? "s" : ""} uploaded` : "Document uploaded";
                    })()}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Block Pre-finance Section */}
      <Box
        sx={{
          mt: 4,
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          bgcolor:
            getPreFinanceStatus().status === "complete" && !blockPreFinance.isBlocked && blockPreFinance.allowPreFinance
              ? "rgba(76, 175, 80, 0.1)"
              : "rgba(244, 67, 54, 0.1)",
          transition: "background-color 0.3s ease",
          opacity: canWriteVfFlags ? 1 : 0.5,
          position: "relative", // Add this for overlay positioning
        }}
      >
        {isDataLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.paper",
              zIndex: 1,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ ...getThemeTextColor(theme) }}>
              Loading required data...
            </Typography>
          </Box>
        )}
        <Typography variant="h6" gutterBottom sx={{ ...getThemeTextColor(theme) }}>
          Pre-finance Settings
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={blockPreFinance.allowPreFinance}
                onChange={handleAllowPreFinanceChange}
                disabled={
                  !canWriteVfFlags ||
                  isDataLoading ||
                  getPreFinanceStatus().status === "incomplete" ||
                  blockPreFinance.isBlocked
                }
              />
            }
            label={<Typography sx={{ ...getThemeTextColor(theme) }}>Enable Pre-Finance</Typography>}
          />
          {!isDataLoading && (getPreFinanceStatus().status === "incomplete" || blockPreFinance.isBlocked) && (
            <Typography variant="body2" sx={{ mt: 1, ...getThemeTextColor(theme) }}>
              {blockPreFinance.isBlocked ? "Cannot enable Pre-Finance while it is blocked" : getPreFinanceStatus().reason}
            </Typography>
          )}
        </FormGroup>
        <Divider sx={{ my: 3 }} />
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={blockPreFinanceTemp.isBlocked}
                onChange={handleBlockPreFinanceChange}
                disabled={
                  !canWriteVfFlags || (!blockPreFinanceTemp.isBlocked && getPreFinanceStatus().status === "incomplete")
                }
              />
            }
            label={<Typography sx={{ ...getThemeTextColor(theme) }}>Block pre-finance</Typography>}
          />
          {blockPreFinanceTemp.isBlocked && (
            <TextField
              fullWidth
              label="Block Reason"
              value={blockPreFinanceTemp.reason}
              onChange={handleBlockReasonChange}
              margin="normal"
              required
              disabled={!canWriteVfFlags}
            />
          )}
          <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveBlockPreFinance}
              disabled={
                !canWriteVfFlags ||
                (blockPreFinanceTemp.isBlocked === blockPreFinance.isBlocked && blockPreFinanceTemp.reason === blockPreFinance.reason)
              }
            >
              Save Block Settings
            </Button>
          </Box>
        </FormGroup>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}>Cancel</Button>
          <Button
            onClick={() => {
              confirmDialog.onConfirm();
              setConfirmDialog((prev) => ({ ...prev, open: false }));
            }}
            color="primary"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* UBO Management Dialog */}
      <Dialog
        open={uboDialog.open && uboDialog.mode === "list"}
        onClose={handleUboClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6">UBO Management</Typography>
          <Button
            disabled={!hasAllowPreFinanceRole()}
            variant="contained"
            color="primary"
            startIcon={<PersonAddIcon />}
            onClick={handleUboAdd}
          >
            Add UBO
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", height: "calc(100% - 64px)" }}>
          {/* Left side - UBO List */}
          <Box
            sx={{
              width: "40%",
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 2,
            }}
          >
            <Stack spacing={2}>
              {ubos.map((ubo) => (
                <Card
                  key={ubo.id}
                  variant="outlined"
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  onClick={() => handleUboSelect(ubo)}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                      <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                        <PersonIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {ubo.fullName}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            icon={ubo.idPassportFileId ? <CheckCircleIcon /> : <WarningIcon />}
                            label="ID Document"
                            color={ubo.idPassportFileId ? "success" : "warning"}
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            icon={ubo.pepFileId ? <CheckCircleIcon /> : <WarningIcon />}
                            label="PEP Declaration"
                            color={ubo.pepFileId ? "success" : "warning"}
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            icon={
                              ubo.noFinancialCrimes &&
                              ubo.noFinancialMisconduct &&
                              ubo.noNegativePublications &&
                              ubo.notOnSanctionList &&
                              ubo.noEnvironmentalViolations &&
                              ubo.noReputationDamage ? (
                                <CheckCircleIcon />
                              ) : (
                                <WarningIcon />
                              )
                            }
                            label="Bad Press Check"
                            color={
                              ubo.noFinancialCrimes &&
                              ubo.noFinancialMisconduct &&
                              ubo.noNegativePublications &&
                              ubo.notOnSanctionList &&
                              ubo.noEnvironmentalViolations &&
                              ubo.noReputationDamage
                                ? "success"
                                : "warning"
                            }
                            variant="outlined"
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Created: {moment(ubo.creationDate).format("YYYY-MM-DD HH:mm")}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {ubos.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 4,
                    color: "text.secondary",
                  }}
                >
                  <PersonIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" gutterBottom>
                    No UBOs Added
                  </Typography>
                  <Typography variant="body2">Click the "Add UBO" button to add a new UBO</Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Right side - Document Preview */}
          <Box
            sx={{
              width: "60%",
              display: "flex",
              flexDirection: "column",
              p: 2,
            }}
          >
            {uboDialog.selectedUbo ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {uboDialog.selectedUbo.fullName}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      disabled={!hasAllowPreFinanceRole()}
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleUboEdit(uboDialog.selectedUbo)}
                    >
                      Edit UBO
                    </Button>
                    <Button
                      disabled={!hasAllowPreFinanceRole()}
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleUboDelete(uboDialog.selectedUbo.id)}
                    >
                      Delete UBO
                    </Button>
                  </Stack>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          ID Document
                        </Typography>
                        {uboDialog.selectedUbo.idPassportFileId ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <DescriptionIcon color="primary" />
                            <Button
                              variant="text"
                              startIcon={<FileDownloadIcon />}
                              onClick={() => downloadFile(uboDialog.selectedUbo.idPassportFileId)}
                            >
                              {documentFilenames[uboDialog.selectedUbo.idPassportFileId] || "Download"}
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No document uploaded
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          PEP Declaration
                        </Typography>
                        {uboDialog.selectedUbo.pepFileId ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <DescriptionIcon color="primary" />
                            <Button
                              variant="text"
                              startIcon={<FileDownloadIcon />}
                              onClick={() => downloadFile(uboDialog.selectedUbo.pepFileId)}
                            >
                              {documentFilenames[uboDialog.selectedUbo.pepFileId] || "Download"}
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No document uploaded
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Bad Press Check Status
                  </Typography>
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={1}>
                        <FormControlLabel
                          control={<Checkbox checked={uboDialog.selectedUbo.noFinancialCrimes} disabled />}
                          label="No financial crimes"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={uboDialog.selectedUbo.noFinancialMisconduct} disabled />}
                          label="No financial misconduct"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={uboDialog.selectedUbo.noNegativePublications} disabled />}
                          label="No negative publications"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={uboDialog.selectedUbo.notOnSanctionList} disabled />}
                          label="Not on sanction list"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={uboDialog.selectedUbo.noEnvironmentalViolations} disabled />}
                          label="No environmental violations"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={uboDialog.selectedUbo.noReputationDamage} disabled />}
                          label="No reputation damage"
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>

                {/* Document Previews */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Document Previews
                  </Typography>
                  <Grid container spacing={2}>
                    {/* ID Document Preview */}
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>
                            ID Document
                          </Typography>
                          {uboDialog.selectedUbo.idPassportFileId ? (
                            <Box sx={{ height: 400, border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                              {uboPreviewUrls.idDocument ? (
                                <iframe
                                  src={uboPreviewUrls.idDocument}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                  }}
                                  title="ID Document preview"
                                />
                              ) : (
                                <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <CircularProgress />
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No document uploaded
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* PEP Declaration Preview */}
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>
                            PEP Declaration
                          </Typography>
                          {uboDialog.selectedUbo.pepFileId ? (
                            <Box sx={{ height: 400, border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                              {uboPreviewUrls.pepDeclaration ? (
                                <iframe
                                  src={uboPreviewUrls.pepDeclaration}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                  }}
                                  title="PEP Declaration preview"
                                />
                              ) : (
                                <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <CircularProgress />
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No document uploaded
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                }}
              >
                <DescriptionIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  Select a UBO to view details
                </Typography>
                <Typography variant="body2">Click on a UBO from the list to view their documents and status</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUboClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* UBO Edit Dialog */}
      <UBODialog
        open={uboDialog.open && uboDialog.mode === "edit"}
        onClose={handleUboClose}
        ubo={uboDialog.ubo}
        onSave={handleUboSubmit}
        customerId={customerId}
      />

      {/* Authorized Signatory Dialog */}
      <Dialog
        open={signatoryDialog.open && signatoryDialog.mode === "list"}
        onClose={handleSignatoryClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6">Authorized Signatories</Typography>
          <Button variant="contained" color="primary" startIcon={<PersonAddIcon />} onClick={handleSignatoryAdd}>
            Add Signatory
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", height: "calc(100% - 64px)" }}>
          {/* Left side - Signatory List */}
          <Box
            sx={{
              width: "40%",
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 2,
            }}
          >
            <Stack spacing={2}>
              {signatories.map((signatory) => (
                <Card
                  key={signatory.id}
                  variant="outlined"
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  onClick={() => handleSignatorySelect(signatory)}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                      <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                        <PersonIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {signatory.fullName}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            icon={signatory.idPassportFileId ? <CheckCircleIcon /> : <WarningIcon />}
                            label="ID Document"
                            color={signatory.idPassportFileId ? "success" : "warning"}
                            variant="outlined"
                          />
                        </Stack>
                        {signatory.comment && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {signatory.comment}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Created: {moment(signatory.creationDate).format("YYYY-MM-DD HH:mm")}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {signatories.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 4,
                    color: "text.secondary",
                  }}
                >
                  <PersonIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" gutterBottom>
                    No Signatories Added
                  </Typography>
                  <Typography variant="body2">Click the "Add Signatory" button to add a new signatory</Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Right side - Document Preview */}
          <Box
            sx={{
              width: "60%",
              display: "flex",
              flexDirection: "column",
              p: 2,
            }}
          >
            {signatoryDialog.selectedSignatory ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {signatoryDialog.selectedSignatory.name}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleSignatoryEdit(signatoryDialog.selectedSignatory)}
                    >
                      Edit Signatory
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleSignatoryDelete(signatoryDialog.selectedSignatory.id)}
                    >
                      Delete Signatory
                    </Button>
                  </Stack>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          ID Document
                        </Typography>
                        {signatoryDialog.selectedSignatory.idPassportFileId ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <DescriptionIcon color="primary" />
                            <Button
                              variant="text"
                              startIcon={<FileDownloadIcon />}
                              onClick={() => downloadFile(signatoryDialog.selectedSignatory.idPassportFileId)}
                            >
                              {documentFilenames[signatoryDialog.selectedSignatory.idPassportFileId] || "Download"}
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No document uploaded
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Document Preview */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Document Preview
                  </Typography>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        ID Document
                      </Typography>
                      {signatoryDialog.selectedSignatory.idPassportFileId ? (
                        <Box sx={{ height: 400, border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                          {signatoryPreviewUrls.idDocument ? (
                            <iframe
                              src={signatoryPreviewUrls.idDocument}
                              style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                              }}
                              title="ID Document preview"
                            />
                          ) : (
                            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <CircularProgress />
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No document uploaded
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                }}
              >
                <DescriptionIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  Select a Signatory to view details
                </Typography>
                <Typography variant="body2">Click on a signatory from the list to view their documents</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSignatoryClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Authorized Signatory Edit Dialog */}
      <AuthorizedSignatoryDialog
        open={signatoryDialog.open && signatoryDialog.mode === "edit"}
        onClose={handleSignatoryClose}
        signatory={signatoryDialog.signatory}
        onSave={handleSignatorySubmit}
        customerId={customerId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog.open} onClose={() => setDeleteConfirmDialog({ open: false, uboId: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this UBO?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, uboId: null })}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View/Upload Dialog */}
      <Dialog
        open={viewDialog.open}
        onClose={handleCloseDialog}
        maxWidth={viewDialog.document?.id === BAD_PRESS_CHECK_ID ? "sm" : "lg"}
        fullWidth
        PaperProps={{
          sx: {
            height: viewDialog.document?.id === BAD_PRESS_CHECK_ID ? "auto" : "80vh",
            maxHeight: viewDialog.document?.id === BAD_PRESS_CHECK_ID ? "600px" : "80vh",
          },
        }}
      >
        <DialogTitle>{viewDialog.document?.name}</DialogTitle>
        <DialogContent>
          {viewDialog.document?.id === BAD_PRESS_CHECK_ID ? (
            // Bad Press Check Dialog Content
            <Box sx={{ p: 2 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!hasAllowPreFinanceRole()}
                      checked={tempBadPressCheck?.isNotUHRCCountry}
                      onChange={handleBadPressCheckChange("isNotUHRCCountry")}
                    />
                  }
                  label="Not subject to UHRC countries"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!hasAllowPreFinanceRole()}
                      checked={tempBadPressCheck?.noFinancialCrimes}
                      onChange={handleBadPressCheckChange("noFinancialCrimes")}
                    />
                  }
                  label="Not involved in financial crimes"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!hasAllowPreFinanceRole()}
                      checked={tempBadPressCheck?.noFinancialMisconduct}
                      onChange={handleBadPressCheckChange("noFinancialMisconduct")}
                    />
                  }
                  label="No fraud, corruption, tax evasion, money laundering or terrorism"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!hasAllowPreFinanceRole()}
                      checked={tempBadPressCheck?.noNegativePublications}
                      onChange={handleBadPressCheckChange("noNegativePublications")}
                    />
                  }
                  label="No negative publications"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!hasAllowPreFinanceRole()}
                      checked={tempBadPressCheck?.notOnSanctionList}
                      onChange={handleBadPressCheckChange("notOnSanctionList")}
                    />
                  }
                  label="Not on any sanction list"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!hasAllowPreFinanceRole()}
                      checked={tempBadPressCheck?.noEnvironmentalViolations}
                      onChange={handleBadPressCheckChange("noEnvironmentalViolations")}
                    />
                  }
                  label="No environmental violations"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!hasAllowPreFinanceRole()}
                      checked={tempBadPressCheck?.noReputationDamage}
                      onChange={handleBadPressCheckChange("noReputationDamage")}
                    />
                  }
                  label="No serious reputation damage"
                />
              </FormGroup>
            </Box>
          ) : (
            // Regular Document Dialog Content
            <Grid container spacing={2} sx={{ height: "100%" }}>
              {/* Left side - Document list and upload area */}
              <Grid item xs={4}>
                <Box
                  sx={{
                    height: "100%",
                    borderRight: 1,
                    borderColor: "divider",
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Document list */}
                  <Box sx={{ flex: 1, overflow: "auto", mb: 2 }}>
                    {selectedDocument?.documents.map((doc) => (
                      <Paper
                        key={doc.customerDocumentId}
                        sx={{
                          p: 2,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          bgcolor:
                            selectedPreviewDoc?.customerDocumentId === doc.customerDocumentId ? "action.selected" : "background.paper",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                        onClick={() => handlePreviewDocument(doc)}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ ...getThemeTextColor(theme) }}>
                            {documentFilenames[doc.fileId] || "Loading..."}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }} display="block">
                            Upload date: {moment(doc.uploadDate).format("DD-MM-YYYY HH:mm")}
                          </Typography>
                          {doc.validFrom && doc.validTill && (
                            <Typography variant="caption" sx={{ color: "text.secondary" }} display="block">
                              Valid: {moment(doc.validFrom).format("DD-MM-YYYY")} - {moment(doc.validTill).format("DD-MM-YYYY")}
                            </Typography>
                          )}
                          {doc.validFrom && !doc.validTill && doc.documentType === SEPA_MANDATE_ID && (
                            <Typography variant="caption" sx={{ color: "text.secondary" }} display="block">
                              Start date: {moment(doc.validFrom).format("DD-MM-YYYY")}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadFile(doc.fileId);
                            }}
                            color="primary"
                            size="small"
                          >
                            <FileDownloadIcon />
                          </IconButton>
                          <IconButton
                            disabled={!hasAllowPreFinanceRole()}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteDocument(doc);
                            }}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Upload area - Only show if no document exists for non-multi-document types */}
                  {!selectedDocument?.type.multiDocument && selectedDocument?.documents.length > 0 ? null : (
                    <Box
                      {...getRootProps()}
                      sx={{
                        border: "2px dashed",
                        borderColor:
                          hasAllowPreFinanceRole()
                            ? "primary.main"
                            : "grey.300",
                        borderRadius: 1,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        bgcolor: isDragActive ? "action.hover" : "background.paper",
                        minHeight: "150px",
                      }}
                    >
                      <input {...getInputProps()} />
                      <CloudUploadIcon
                        sx={{
                          fontSize: 32,
                          color:
                            hasAllowPreFinanceRole()
                              ? "primary.main"
                              : "grey.300",
                          mb: 1,
                        }}
                      />
                      <Typography variant="body1" align="center" gutterBottom>
                        {!hasAllowPreFinanceRole()
                          ? "You don't have permission to upload this document type"
                          : isDragActive
                          ? "Drop the file here"
                          : "Drag and drop a file here, or click to select a file"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" align="center">
                        Supported formats: PDF, DOC, DOCX
                      </Typography>
                      {selectedFile && (
                        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                          Selected file: {selectedFile.name}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Right side - Preview area */}
              <Grid item xs={8}>
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  {previewUrl ? (
                    <iframe
                      src={previewUrl}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                      title="Document preview"
                    />
                  ) : (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "action.disabled",
                      }}
                    >
                      <Typography variant="h6" color="textSecondary">
                        Select a document to preview
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {viewDialog.document?.id === BAD_PRESS_CHECK_ID ? (
            <Button onClick={handleBadPressCheckSubmit} disabled={!hasAllowPreFinanceRole()} variant="contained" color="primary">
              Save Changes
            </Button>
          ) : (
            <Button onClick={handleCloseDialog}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={dateInputDialog.open} onClose={handleDateInputCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedDocument?.type?.requiresStartDate ? "Start Date Required" : "Validity Period Required"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {selectedDocument?.type?.requiresStartDate
              ? "Please provide the start date for the SEPA Mandate:"
              : "Please provide the validity period for the Tax Compliance Certificate:"}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={selectedDocument?.type?.requiresStartDate ? 12 : 6}>
              <DatePicker
                label={selectedDocument?.type?.requiresStartDate ? "Start Date" : "Valid From"}
                inputFormat="dd-MM-yyyy"
                mask="__-__-____"
                value={dateInputDialog.validFrom}
                onChange={(value) => setDateInputDialog((prev) => ({ ...prev, validFrom: value }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    required
                    placeholder="dd-mm-yyyy"
                    inputProps={{
                      ...params.inputProps,
                      "aria-label": selectedDocument?.type?.requiresStartDate ? "Start date" : "Valid from date",
                    }}
                  />
                )}
              />
            </Grid>
            {!selectedDocument?.type?.requiresStartDate && (
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Valid Till"
                  inputFormat="dd-MM-yyyy"
                  mask="__-__-____"
                  value={dateInputDialog.validTill}
                  onChange={(value) => setDateInputDialog((prev) => ({ ...prev, validTill: value }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      placeholder="dd-mm-yyyy"
                      inputProps={{ ...params.inputProps, "aria-label": "Valid till date" }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDateInputCancel}>Cancel</Button>
          <Button onClick={handleDateInputConfirm} variant="contained" color="primary">
            Upload Document
          </Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
};

export default CompanyDocumentsTab;
